import * as React from 'react';
import { type PhoneCallView, usePhoneCallsByCompany } from './usePhoneCallsByCompany';

/**
 * Extract the set of dialed E.164 numbers from a lead's past phone calls. Reads
 * `phoneCall.phoneNumber.e164` (the dialed-number metadata on `IPhoneCall`);
 * calls without a number are skipped and duplicates collapse into the Set.
 */
export function collectDialedE164s(phoneCalls: readonly PhoneCallView[]): Set<string> {
	const dialed = new Set<string>();
	for (const view of phoneCalls) {
		const e164 = view.phoneCall?.phoneNumber?.e164;
		if (e164) dialed.add(e164);
	}
	return dialed;
}

export type UseDialedNumbersApi = {
	/** Every number dialed for this lead — seeded from call history plus this session's dials. */
	dialedNumbers: ReadonlySet<string>;
	/** Optimistically record a freshly-dialed number so its phone icon greens immediately. */
	markDialed: (e164: string) => void;
};

/**
 * Tracks which of a lead's phone numbers have been dialed so the UI can green
 * the matching phone icon (#18844). Seeds from the lead's transcribed call
 * history (`usePhoneCallsByCompany`) so prior calls survive a reload, and layers
 * an optimistic set for dials placed this session (the history query is
 * transcription-filtered and lags a just-placed call). The optimistic set resets
 * when the lead changes so one lead's session dials never bleed into the next.
 */
export function useDialedNumbers(companyId: string | undefined): UseDialedNumbersApi {
	const { data } = usePhoneCallsByCompany(companyId);
	const [optimistic, setOptimistic] = React.useState<ReadonlySet<string>>(() => new Set<string>());

	// New lead → drop the prior lead's session dials; its green state should come
	// only from the new lead's own history + dials.
	React.useEffect(() => {
		setOptimistic(new Set<string>());
	}, [companyId]);

	const seeded = React.useMemo(() => collectDialedE164s(data ?? []), [data]);

	const dialedNumbers = React.useMemo(() => {
		const merged = new Set(seeded);
		for (const e164 of optimistic) merged.add(e164);
		return merged;
	}, [seeded, optimistic]);

	const markDialed = React.useCallback((e164: string) => {
		setOptimistic(prev => {
			if (prev.has(e164)) return prev;
			const next = new Set(prev);
			next.add(e164);
			return next;
		});
	}, []);

	return { dialedNumbers, markDialed };
}
